import { ReactElement, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import File from '@components/DesignLibrary/File';
import Shimmer from '@components/Shimmer';
import preloadDocument from '@helpers/preloadDocument';
import { useUser } from '@hooks';
import { logError } from '@providers/ErrorTracking';
import { useFeatureFlag } from '@providers/FeatureFlags';
import * as ApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import { pdf, StyleSheet, Document, Page, View, Image as ImageForPDF } from '@react-pdf/renderer';
import useNoodleApi from '@hooks/useNoodleApi';
import { updateImageOrientation } from '@tsClient/documents';
import ProgressIndicator from '@/components/ProgressIndicator';

import { ArrowClockwise } from '@phosphor-icons/react';
import Button from '@/components/DesignLibrary/Button';
import { nanoid } from 'nanoid';
import AIReaderModal from './AIReaderModal';
import Content from './Content';
import s from './DocumentPreview.module.scss';
import ExtractedDataModal from './ExtractedDataModal';

type Props = {
  allowRename?: boolean;
  document: Pick<ApiModels.Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt'> | null;
  accessToken: string | null;
  canDownload: boolean;
  onReject?: () => void;
  onClose?: () => void;
  isFaded?: boolean;
  documentRequestFileDescription?: string | null;
  documentRequestFileName?: string | null;
  canEvaluateWithAi?: boolean;
  internalDataType?: string | null;
  onRename?: () => Promise<void>;
  onDownloadCSV?: () => void;
  fileDocumentId?: string;
};

/**
 * Converts a Blob object to a base64 encoded string representation.
 * The canvas is used to strip the image of any metadata.
 * Returns the raw base64 string if the canvas context is unavailable.
 *
 * @param blob - The Blob object to convert
 * @returns Promise that resolves with the base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {

      const img = new Image();
      img.src = reader.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(reader.result as string);
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0, img.width, img.height);
        const base64 = canvas.toDataURL('image/jpeg', 1);
        resolve(base64);
      };

      img.onerror = () => resolve(reader.result as string);
    };

    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

const pdfStyles = StyleSheet.create({
  image: {
    height: '100%',
    objectFit: 'contain',
    width: '100%',
  },
  page: {
    padding: 20,
  },
});

const ImageToPdf = ({ imageSrc }: { imageSrc: string }): ReactElement => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View>
        <ImageForPDF src={imageSrc} style={pdfStyles.image} />
      </View>
    </Page>
  </Document>
);

const DocumentPreview: React.FC<Props> = ({
  allowRename = true,
  document,
  accessToken,
  canDownload,
  onReject,
  onClose,
  isFaded,
  documentRequestFileDescription,
  documentRequestFileName,
  canEvaluateWithAi = false,
  internalDataType,
  onRename,
  onDownloadCSV,
  fileDocumentId,
}) => {
  const [user] = useUser();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [base64Blob, setBase64Blob] = useState<string | null>(null);
  const [rotationClicked, setRotationClicked] = useState(false);
  const [aiReaderModalIsOpen, setAiReaderModalIsOpen] = useState(false);
  const [extractedDataModalIsOpen, setExtractedDataModalIsOpen] = useState(false);
  const canUseAiReaderFeatureFlag = useFeatureFlag('can-use-ai-reader');

  const canUseAiReader = canUseAiReaderFeatureFlag && canEvaluateWithAi;

  const { getData: rotateImage } = useNoodleApi(updateImageOrientation);

  const loadPreview = useCallback(async (): Promise<void> => {
    if (!document?.id || !document.mimeType) { return; }
    preloadDocument({
      accessToken,
      id: document.id,
    })
      .then(blob => {
        const typeBlob = new Blob([blob], { type: document.mimeType });
        const blobPreviewUrl = window.URL.createObjectURL(typeBlob);
        blobToBase64(blob).then(res => {
          // The API doesn't take `application/octet-stream`
          const remimedBase64Blob = res.replace('application/octet-stream', document.mimeType);

          setBase64Blob(remimedBase64Blob);
        });

        setPreviewUrl(blobPreviewUrl);
      })
      .catch(error => {
        logError(error);
      });
  }, [accessToken, document?.id, document?.mimeType]);

  useEffect(() => {
    if ((accessToken || user?.id) && document?.id) {
      loadPreview();
    }
  }, [document?.id, accessToken, user?.id, document?.mimeType, loadPreview]);

  const downloadFile = async (): Promise<void> => {
    if (!previewUrl || !base64Blob || !document) return;

    try {
      const isPdf = document?.mimeType === 'application/pdf';
      const isImage = document?.mimeType.startsWith('image/');

      let blob: Blob;
      let fileName = document?.fileName || 'document';

      if (isPdf) {
        blob = await fetch(previewUrl).then(res => res.blob());
      } else if (isImage) {
        // Convert image to PDF
        blob = await pdf(<ImageToPdf imageSrc={base64Blob} />).toBlob();
        fileName = `${fileName}.pdf`;
      } else {
        // non-PDF/non-image files
        blob = await fetch(previewUrl).then(res => res.blob());
      }

      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = fileName;
      window.document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      logError(error);
    }
  };

  const download = canDownload ? downloadFile : undefined;

  const onOpenAiReader = (): void => setAiReaderModalIsOpen(true);
  const onOpenExtractedData = (): void => setExtractedDataModalIsOpen(true);

  async function rotateImageBlob(base64: string, mimeType: string): Promise<{ rotatedBlob: Blob; rotatedBase64: string } | null> {
    setBase64Blob(null);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64;

      img.onload = () => {
        const canvas = window.document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        canvas.width = img.height;
        canvas.height = img.width;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
              const rotatedBase64 = reader.result as string;
              resolve({ rotatedBase64, rotatedBlob: blob });
            };
            reader.onerror = () => reject(new Error('Failed to read blob as base64'));
            reader.readAsDataURL(blob);
          },
          mimeType,
          1.0,
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
    });
  }

  async function rotateRight(): Promise<void> {
    if (!previewUrl || !base64Blob || !document) {
      return;
    }

    setRotationClicked(true);

    try {
      // Rotate locally first for immediate feedback
      const rotationResult = await rotateImageBlob(base64Blob, document.mimeType);
      if (!rotationResult) {
        throw new Error('Rotation failed');
      }

      const { rotatedBlob } = rotationResult;

      // Update the UI with the locally rotated image
      setPreviewUrl(URL.createObjectURL(rotatedBlob));

      // Rotate on the server
      await rotateImage({
        direction: 'right',
        documentId: document.id,
      });

      // Reload the preview from the server to ensure consistency
      await loadPreview();
    } catch (error) {
      logError(error);
      // Revert to the original image if rotation fails
      await loadPreview();
    }

    setRotationClicked(false);
  }

  if (!previewUrl) {
    return <Shimmer className={s.iframe} />;
  }

  return (
    <>
      <File
        document={document}
        onDownload={download}
        allowRename={allowRename}
        onRename={onRename}
        onReject={onReject}
        onClose={onClose}
        onOpenAiReader={canUseAiReader ? onOpenAiReader : undefined}
        onOpenExtractedData={internalDataType ? onOpenExtractedData : undefined}
        key={document ? (document.id + document.fileName) : nanoid()}
        showCorrection={user?.isCreator}
        onDownloadCSV={onDownloadCSV}
      />
      <div className={s.container}>
        <div className={classNames(s.wrapper, isFaded && s.faded)}>
          {document?.mimeType.includes('image') && (
            <Button variant="secondary" size="xs" onClick={rotateRight} disabled={!base64Blob}>
              <div className={s.rotateIcon}>{(!base64Blob && rotationClicked) ? <ProgressIndicator size={12} /> : <ArrowClockwise />}</div> Rotat
              {(!base64Blob && rotationClicked) ? 'ing...' : 'e'}
            </Button>
          )}
          <Content url={previewUrl} mimeType={document?.mimeType || ''} />
        </div>
      </div>
      {aiReaderModalIsOpen && document &&(
        <AIReaderModal
          onClose={() => setAiReaderModalIsOpen(false)}
          base64Blob={base64Blob}
          documentId={document.id}
          documentRequestFileDescription={documentRequestFileDescription}
          documentRequestFileName={documentRequestFileName}
        />
      )}
      {extractedDataModalIsOpen&& document && fileDocumentId
        && <ExtractedDataModal onClose={() => setExtractedDataModalIsOpen(false)} documentId={fileDocumentId} />}
    </>
  );
};

export default DocumentPreview;
